// Central place that decides how an agency's brand colour (the "agent colour")
// is rendered across the marketing site. Pale brand colours — e.g. a light cyan
// — break the things that paint white text/fills on top of the colour: the
// onboarding animation, the branded buttons and the bottom insights-hub banner.
//
// Everything keys off a single measured value, `agentColourLightness`, so the
// behaviour is consistent: measure the colour's lightness once, then derive a
// readable variant from it. Dark brand colours pass through unchanged, so
// agencies that already look right are unaffected.

function parseHex(hex) {
  const m = (hex || "").replace("#", "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  if (full.length !== 6) return null;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
}

function lightnessOf({ r, g, b }) {
  // Perceived (relative) luminance, 0 (black) .. 1 (white).
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

// Vendorleads green — the fallback when an agency has no colour set.
const FALLBACK_RGB = { r: 16, g: 185, b: 129 };

// Above this lightness, white text/fills on the colour stop being legible.
export const AGENT_COLOUR_LIGHT_THRESHOLD = 0.62;

// When we darken a too-light colour, aim its lightness at this target so the
// result is solid enough for white text while keeping the brand hue.
const DARKEN_TARGET = 0.42;

// 0 (black) .. 1 (white): the lightness of an agent colour. This is the single
// value every other helper in this module is derived from.
export function agentColourLightness(hex) {
  const rgb = parseHex(hex);
  // Unknown colours are treated as dark, so we default to white text on them.
  return rgb ? lightnessOf(rgb) : 0;
}

export function isLightAgentColour(hex) {
  return agentColourLightness(hex) > AGENT_COLOUR_LIGHT_THRESHOLD;
}

// The readable rgb channels for a brand colour: light colours are darkened in
// proportion to how light they are; dark colours are returned untouched.
function readableRgb(hex) {
  const rgb = parseHex(hex) || FALLBACK_RGB;
  const lum = lightnessOf(rgb);
  if (lum <= AGENT_COLOUR_LIGHT_THRESHOLD) return rgb;
  const f = DARKEN_TARGET / lum;
  return {
    r: Math.round(rgb.r * f),
    g: Math.round(rgb.g * f),
    b: Math.round(rgb.b * f),
  };
}

// A solid version of the agent colour, safe to use either as a background behind
// white text or as text/icons on a white background. Drop-in for the raw hex.
export function agentSolidColour(hex) {
  const { r, g, b } = readableRgb(hex);
  return `rgb(${r}, ${g}, ${b})`;
}

// A translucent tint of the (readable) agent colour. Drop-in replacement for
// hexToRgba(hex, alpha) that also holds up when the brand colour is light.
export function agentColourAlpha(hex, alpha) {
  const { r, g, b } = readableRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Text colour that stays legible *on top of* the solid agent colour. Because
// agentSolidColour is always dark enough, this is white — exposed as a helper so
// call sites read clearly and stay correct if the darkening target ever changes.
export function agentTextOn() {
  return "#fff";
}
